<!--
  A show, as a card with its artwork.

  V1 presented shows this way and it was the right call: a station has a handful of shows,
  each with strong artwork, and a table of five rows throws that away. The artwork is how a
  presenter recognises their own show at a glance.

  The scheduling state is a pill on the card rather than a column, because it is the one
  property that decides whether the show appears in next week's schedule at all.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { ICONS } from '../icons.js';

const props = withDefaults(
  defineProps<{
    name: string;
    slug: string;
    artworkUrl?: string | null;
    schedulingEnabled: boolean;
    archived?: boolean;
    /** Free text under the pills, e.g. "Every Friday · 60 min". */
    cadence?: string | null;
    episodeCount?: number | null;
    /**
     * Every user-visible word, passed in.
     *
     * This package has no opinion about which language the panel is in and no dependency
     * on its i18n library: it is a component library and should be usable from anything.
     * The caller translates; the component lays out.
     */
    labels: {
      schedulingOn: string;
      schedulingOff: string;
      archived: string;
      episodes: string;
      open: string;
    };
  }>(),
  { archived: false },
);

const emit = defineEmits<{ (event: 'open'): void }>();

/** Initials, so a show with no artwork still has something recognisable. */
const initials = computed(() =>
  props.name
    .split(/\s+/)
    .slice(0, 3)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join(''),
);
</script>

<template>
  <article class="shio-show" :class="{ 'shio-show--archived': archived }">
    <div class="shio-show__art">
      <img v-if="artworkUrl" :src="artworkUrl" :alt="`${name} artwork`" loading="lazy" />
      <span v-else class="shio-show__initials" aria-hidden="true">{{ initials }}</span>
    </div>

    <div class="shio-show__body">
      <h3 class="shio-show__name">{{ name }}</h3>
      <p class="shio-show__slug">{{ slug }}</p>

      <p class="shio-show__pills">
        <span v-if="archived" class="shio-show__pill shio-show__pill--muted">
          {{ labels.archived }}
        </span>
        <span v-else-if="schedulingEnabled" class="shio-show__pill shio-show__pill--on">
          {{ labels.schedulingOn }}
        </span>
        <!-- Not a neutral state: a show with scheduling off produces no slots at all. -->
        <span v-else class="shio-show__pill shio-show__pill--off">{{ labels.schedulingOff }}</span>

        <span v-if="episodeCount !== null && episodeCount !== undefined" class="shio-show__pill">
          {{ labels.episodes }}
        </span>
      </p>

      <p v-if="cadence" class="shio-show__cadence">{{ cadence }}</p>
    </div>

    <button
      class="shio-btn shio-btn--ghost shio-show__action"
      type="button"
      :aria-label="labels.open"
      @click="emit('open')"
    >
      <component :is="ICONS.ChevronRight" :size="16" aria-hidden="true" />
    </button>
  </article>
</template>

<style scoped>
.shio-show {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--shio-space-3);
  padding: var(--shio-space-3);
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-elevated));
}

.shio-show--archived {
  opacity: 0.65;
}

.shio-show__art {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  overflow: hidden;
  border-radius: var(--shio-radius-sm);
  background: hsl(var(--shio-surface-base));
  border: 1px solid hsl(var(--shio-border));
}

.shio-show__art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shio-show__initials {
  font-family: var(--shio-font-display);
  font-size: var(--shio-type-section-size);
  color: hsl(var(--shio-text-muted));
}

.shio-show__body {
  min-width: 0;
}

.shio-show__name {
  margin: 0;
  font-family: var(--shio-font-display);
  font-size: var(--shio-type-card-title-size);
  line-height: var(--shio-type-card-title-line);
  color: hsl(var(--shio-text-primary));
}

.shio-show__slug,
.shio-show__cadence {
  margin: 0;
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
}

.shio-show__pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--shio-space-1);
  margin: var(--shio-space-2) 0 var(--shio-space-1);
}

.shio-show__pill {
  padding: 1px var(--shio-space-2);
  border: 1px solid hsl(var(--shio-border));
  border-radius: 999px;
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-badge-size);
  color: hsl(var(--shio-text-secondary));
}

.shio-show__pill--on {
  border-color: hsl(var(--shio-status-success) / 0.5);
  color: hsl(var(--shio-status-success));
}

.shio-show__pill--off {
  border-color: hsl(var(--shio-status-warning) / 0.5);
  color: hsl(var(--shio-status-warning));
}

.shio-show__pill--muted {
  color: hsl(var(--shio-text-muted));
}

@media (max-width: 520px) {
  .shio-show__art {
    width: 56px;
    height: 56px;
  }
}
</style>
